import { z } from 'zod';

/**
 * One permission of the catalog as the editor lists it.
 */
export type PermissionView = { key: string; resource: string; description: string };

/**
 * A permission template of the school, with what it grants and how many people hold it.
 */
export type RoleTemplateView = {
  id: string;
  name: string;
  description: string | null;
  isSystemDefault: boolean;
  permissionKeys: string[];
  usersCount: number;
};

/**
 * A person of the school as the users list shows them.
 */
export type SchoolUserView = {
  id: string;
  email: string | null;
  givenName: string;
  familyName: string;
  status: string;
  roles: string[];
  templates: { id: string; name: string }[];
  overridesCount: number;
  hasAccount: boolean;
};

/**
 * Where each of a person's permissions comes from: their templates, the exceptions someone set on
 * them, and the set that results and that the API actually enforces.
 */
export type UserAccessView = {
  userId: string;
  templates: { id: string; name: string; permissionKeys: string[] }[];
  overrides: { permissionKey: string; granted: boolean }[];
  effective: { all: boolean; keys: readonly string[] };
};

/**
 * Body of `POST /users`: a member of the school's staff, invited with the templates they hold.
 */
export const createSchoolUserInputSchema = z.object({
  givenName: z.string().trim().min(1).max(80),
  familyName: z.string().trim().min(1).max(80),
  email: z.string().trim().email().max(160),
  templateIds: z.array(z.string().uuid()).max(10).default([]),
  invite: z.boolean().default(true),
});

/**
 * Body of `PATCH /users/{id}`: the name, or whether the person may still sign in.
 */
export const updateSchoolUserInputSchema = z
  .object({
    givenName: z.string().trim().min(1).max(80),
    familyName: z.string().trim().min(1).max(80),
    status: z.enum(['active', 'disabled']),
  })
  .partial()
  .refine((input) => Object.keys(input).length > 0, 'nothing to update');

/**
 * Body of `POST /roles` and `PATCH /roles/{id}`: a template and what it grants.
 */
export const roleTemplateInputSchema = z.object({
  name: z.string().trim().min(1).max(60),
  description: z.string().trim().max(200).nullish(),
  permissionKeys: z.array(z.string().trim().min(3).max(60)).max(80),
});

/**
 * Body of `PUT /users/{id}/roles`: the templates the person holds, as a whole set.
 */
export const setUserTemplatesInputSchema = z.object({
  templateIds: z.array(z.string().uuid()).max(10),
});

/**
 * Body of `PUT /users/{id}/permissions`: the exceptions on one person, as a whole set.
 * `granted: true` adds a permission their templates do not give; `false` takes one away.
 */
export const setUserOverridesInputSchema = z.object({
  overrides: z
    .array(z.object({ permissionKey: z.string().trim().min(3).max(60), granted: z.boolean() }))
    .max(80),
});

/**
 * Parsed body of `POST /users`.
 */
export type CreateSchoolUserInput = z.infer<typeof createSchoolUserInputSchema>;

/**
 * Parsed body of `PATCH /users/{id}`.
 */
export type UpdateSchoolUserInput = z.infer<typeof updateSchoolUserInputSchema>;

/**
 * Parsed body of the role template routes.
 */
export type RoleTemplateInput = z.infer<typeof roleTemplateInputSchema>;

/**
 * Parsed body of `PUT /users/{id}/roles`.
 */
export type SetUserTemplatesInput = z.infer<typeof setUserTemplatesInputSchema>;

/**
 * Parsed body of `PUT /users/{id}/permissions`.
 */
export type SetUserOverridesInput = z.infer<typeof setUserOverridesInputSchema>;
